import type {
  ScenarioTimeline_CoaFragment,
  ScenarioTimeline_OrganizationFragment,
  ScenarioTimeline_PortCallFragment,
} from '../../graphql';
import type { Range } from '../../lib/util';
import {
  assertDefined,
  get,
  isDefined,
  isInRange,
  mapKeyBy,
  sortBy,
} from '../../lib/util';
import type { PortCallData } from './Core';
import { EventCategory, getEventLocation } from './Core';
import { getEventLocationId, isStaticEvent } from './TimeData';
import type { Vessel } from './Vessel';
import type {
  CargoEvent,
  CoaEvent,
  Event,
  Leg,
  SpotEvent,
  Events,
  EventsAndLegs,
  WaitEvent,
  CoaTimeDataGroup,
  SpotTimeDataGroup,
  TcTimeDataGroup,
  TimeDataInputs,
  TimeDataTotals,
  TimeData,
} from './TimeData';
import {
  PortCallEventType,
  isOffHireEvent,
  hasOrder,
  getEventEndLocationId,
  getEventStartLocationId,
  isLeg,
  isNotTimeData,
  isPortCallArrivalEventTimeData,
  isTimeData,
  ImplicitEventType,
  isCargoEventTimeData,
  isPortCallEventTimeData,
} from './TimeData';
import type { ScheduleDataContext } from './ScheduleDataContext';
import { hToMs } from '../../lib/time';
import { getOverlappingEvents, getPreferredCapacityQuantity } from './util';
import uniqBy from 'lodash/uniqBy';
import findLastIndex from 'lodash/findLastIndex';
import type { ShipmentModalNavTarget } from '../shipment/shipment';
import type { ClientId } from './ClientId';
import { generateClientId } from './ClientId';
import type { ScenarioOrder, ScenarioShipment } from '../../lib/planningData';
import type { DefaultMap } from '@seaber/common/lib/collections';
import { mapGroupBy, mapGroupBy2 } from '@seaber/common/lib/collections';

/**
 * Defines how shipments should behave in relation to others on the same timeline.
 * When true, shipments don't affect each other in any way, they are treated as isolated and individual timelines themselves.
 * When false, a shipment will have to for example wait for previous shipment to complete and there will be ballast in between.
 * Note: TC is not wired to use this setting currently. The TC setting is here more for documentation.
 */
export const shipmentIsolationSetting: {
  [T in EventTimeline['type']]: { withVessel: boolean; withoutVessel: boolean };
} = {
  coa: { withVessel: true, withoutVessel: true },
  spot: { withVessel: true, withoutVessel: true },
  tc: { withVessel: false, withoutVessel: true },
};

export const getSubTimeline = <T extends Events>(
  timeline: EventTimeline<T>,
  focus?: TimelineFocus, // OR filter, only one of the focus params needs to match for an event to be included in the resulting timeline
): EventTimeline<T> => {
  if (!focus) {
    return timeline;
  }
  const { shipmentIds, orderIds, time } = focus;
  // On contracts without vessel assignment we assume that each shipment is carried on a vessel of their own, with no interaction with other orders
  const isolatedShipments =
    (timeline.vesselId === null
      ? shipmentIsolationSetting[timeline.type].withoutVessel
      : shipmentIsolationSetting[timeline.type].withVessel) &&
    (focus.orderIds ?? []).length == 0;

  if (
    orderIds === undefined &&
    shipmentIds === undefined &&
    time === undefined
  ) {
    console.warn(
      `Requested sub timeline with focus but focus has no selective properties present`,
    );
    return timeline;
  }

  const newVoyageGroups = timeline.voyageGroups
    .filter(
      (voyageGroup) =>
        (!orderIds && !shipmentIds) ||
        voyageGroup.orders.some(
          (o) =>
            orderIds?.includes(o.id) ||
            (o.shipment?.clientId &&
              shipmentIds?.includes(o.shipment.clientId)),
        ),
    )
    .map((voyageGroup) => {
      const { eventsAndLegs } = voyageGroup;

      const shipmentsStartingOutsideRange = new Set();
      const portCallsStartingWithinTimeRange = new Set();

      const focusedOrderTds: TimeData<CargoEvent>[] = eventsAndLegs.flatMap(
        (td, i) => {
          if (!isTimeData(td)) return [];

          const prev = eventsAndLegs[i - 1];
          const prevBallastLegStart =
            prev && isLeg(prev) && !prev.laden ? prev.start : null;

          if (
            time?.startingWithinRangeOnly &&
            isPortCallEventTimeData(td) &&
            td.event.portCallId !== null &&
            td.event.event === PortCallEventType.Arrival &&
            isInRange(prevBallastLegStart || td.start, time.range)
          ) {
            // If startingWithinRangeOnly is true, we don't want partial shipments to be
            // included in the resulting sub timeline.
            // We include a complete shipment in the result if its' initial ballast
            // leg (or first arrival if no ballast leg) starts within the range
            // thus we need to separately track which port calls start within the range.
            portCallsStartingWithinTimeRange.add(td.event.portCallId);
          }

          if (!isCargoEventTimeData(td)) return [];

          const { shipmentId } = td.event.order;

          if (time) {
            if (
              time.startingWithinRangeOnly &&
              !shipmentsStartingOutsideRange.has(shipmentId) &&
              portCallsStartingWithinTimeRange.has(td.event.portCallId)
            ) {
              return [td];
            } else if (
              !time.startingWithinRangeOnly &&
              (isInRange(td.start, time.range) || isInRange(td.end, time.range))
            ) {
              return [td];
            }

            if (time.startingWithinRangeOnly && shipmentId != null) {
              shipmentsStartingOutsideRange.add(shipmentId);
            }
          }

          if (orderIds?.includes(td.event.order.id)) {
            return [td];
          }

          if (
            td.event.order.shipment?.clientId &&
            shipmentIds?.includes(td.event.order.shipment.clientId)
          ) {
            return [td];
          }

          return [];
        },
      );

      const focusedPortCallIds = new Set(
        focusedOrderTds.map((td) => td.event.portCallId),
      );
      const focusedOrderIds = new Set(
        focusedOrderTds.map((td) => td.event.order.id),
      );
      focusedPortCallIds.delete(null);

      let currentStartIdx = eventsAndLegs.findIndex(
        (td) =>
          isTimeData(td) &&
          (focusedPortCallIds.has(td.event.portCallId) ||
            (isCargoEventTimeData(td) &&
              focusedOrderIds.has(td.event.order.id))),
      );
      let currentEndIdx = findLastIndex(
        eventsAndLegs,
        (td) =>
          isTimeData(td) &&
          (focusedPortCallIds.has(td.event.portCallId) ||
            (isCargoEventTimeData(td) &&
              focusedOrderIds.has(td.event.order.id))),
      );

      if (currentStartIdx === -1 || currentEndIdx === -1) {
        // This can happen if focusing orders which are not (anymore?) on the timeline. Especially when viewing a shipment modal and unassigning.
        // Make sure both ends are at an index where following steps will find nothing to include in the set
        currentStartIdx = eventsAndLegs.length;
        currentEndIdx = -1;
      }

      const includeBorderLegs = timeline.vesselId !== null;

      let legOrOtherOrderFound = false;
      // Extend to the past from the first order in shipment to include any preceding single events and legs but no more - and exclude even legs if they're not on the same vessel
      // Updated rules: also stop at arrival to port to fix including unrelated events happening during the same port call
      for (let i = currentStartIdx - 1; i >= 0; i--) {
        const td = eventsAndLegs[i];
        if (isLeg(td)) {
          legOrOtherOrderFound = true;
          if (!includeBorderLegs) {
            break;
          }
        } else if (isTimeData(td) && isCargoEventTimeData(td)) {
          // Other order should never be included
          legOrOtherOrderFound = true;
          break;
        } else if (
          isTimeData(td) &&
          isPortCallEventTimeData(td) &&
          td.event.event === PortCallEventType.Arrival
        ) {
          // Arrival found, include this one and stop looking
          currentStartIdx = i;
          break;
        } else if (legOrOtherOrderFound) {
          break;
        }
        currentStartIdx = i;
      }

      // Extend to the future from the last order in shipment until the next leg (or unrelated cargo event) which is not relevant to this shipment anymore
      // Updated rules: also stop at departure from port to fix including unrelated events happening during the same port call
      for (let i = currentEndIdx + 1; i < eventsAndLegs.length; i++) {
        const td = eventsAndLegs[i];
        if (isLeg(td) || (isTimeData(td) && isCargoEventTimeData(td))) {
          // Leg or other order found, not to be included
          break;
        } else if (
          isTimeData(td) &&
          isPortCallEventTimeData(td) &&
          td.event.event === PortCallEventType.Departure
        ) {
          // Departure found, include and stop looking
          currentEndIdx = i;
          break;
        }
        currentEndIdx = i;
      }

      // Collect all events and legs in the range except those that belong to orders of other shipments
      const selectedEventsAndLegs: EventsAndLegs = [];

      for (let i = currentStartIdx; i <= currentEndIdx; ++i) {
        const td = eventsAndLegs[i];
        if (!td) {
          continue;
        }

        const matchesFocus =
          (shipmentIds === undefined && orderIds === undefined) ||
          !isTimeData(td) ||
          (!isCargoEventTimeData(td) &&
            focusedPortCallIds.has(td.event.portCallId)) ||
          (isCargoEventTimeData(td) &&
            (orderIds?.includes(td.event.order.id) ||
              (td.event.order.shipment?.clientId &&
                shipmentIds?.includes(td.event.order.shipment.clientId))));

        // Filter out all cargo events from other shipments if startingWithinRangeOnly is true
        const shipmentInTimeScope =
          !time?.startingWithinRangeOnly ||
          !isTimeData(td) ||
          !isCargoEventTimeData(td) ||
          focusedOrderIds.has(td.event.order.id);

        if ((matchesFocus || !isolatedShipments) && shipmentInTimeScope) {
          selectedEventsAndLegs.push(td);
        }
      }
      const selectedTds = selectedEventsAndLegs.filter(isTimeData);
      const orders = uniqBy(
        selectedTds.flatMap((td) =>
          hasOrder(td.event) ? [td.event.order] : [],
        ),
        'id',
      );

      const portCallsByID = mapKeyBy(voyageGroup.portCalls, (p) => p.id);
      const portCalls = uniqBy(
        selectedTds
          .map((td) => {
            const pcId = td.event.portCallId;
            return pcId && portCallsByID.get(pcId);
          })
          .filter(isDefined),
        (p) => p.id,
      );

      const res: VoyageGroup = {
        eventsAndLegs: selectedEventsAndLegs,
        events: selectedTds,
        orders,
        portCalls,
        legs: selectedEventsAndLegs.filter(isLeg),
        totals: voyageGroup.totals, // TODO: maybe need to recalculate for this sub selection
        inputs: voyageGroup.inputs,
        vessel: voyageGroup.vessel,
      };

      return res;
    });

  return {
    ...timeline,
    isPartial: true,
    isCollapsed: false,
    canCollapse: false,
    parent: timeline,
    voyageGroups: newVoyageGroups as VoyageGroup<T>[],
  };
};

// Shipments within a shipment group can affect each others timings - different shipment groups cannot affect each other
export type VoyageGroup<EventType extends Events = Events> = {
  events: TimeData<EventType>[];
  legs: Array<Leg>;
  portCalls: PortCallData[];
  orders: ScenarioOrder[];
  eventsAndLegs: Array<EventsAndLegs<EventType>[number]>;
  totals: TimeDataTotals | null;
  inputs: TimeDataInputs | null;
  vessel: Vessel | null; // Redundant with EventTimeline but lets us not pass around the timeline object when we have only the VG
};

export type EventTimeline<EventType extends Events = Events> = {
  id: string;
  title: string;
  subtitle: string | null;
  /** When we have focused a subsection of a timeline. Not used currently but seems like a good idea to mark it somehow. */
  isPartial: boolean;
  canCollapse?: boolean;
  isCollapsed?: boolean;
  voyageGroups: VoyageGroup[];
  parent?: EventTimeline<EventType>;
} & (
  | {
      type: 'tc';
      vesselId: number;
      vessel: Vessel;
    }
  | ({
      type: 'coa';
      coa: ScenarioTimeline_CoaFragment;
      coaId: number;
      vessel: Vessel | null;
      vesselId: number | null;
      ownerOrganizationId: number | null;
      canCollapse: boolean;
      isCollapsed: boolean;
    } & (
      | {
          vesselTimelineIds: string[]; // If parent, has these children contract lines
        }
      | {
          contractTimelineId: string; // If child, has this parent contract line
        }
    ))
  | ({
      type: 'spot';
      ownerOrganizationId: number | null;
      vessel: Vessel | null;
      vesselId: number | null;
      canCollapse: boolean;
      isCollapsed: boolean;
    } & (
      | {
          vesselTimelineIds: string[]; // If parent has these children contract lines
        }
      | {
          contractTimelineId: string; // If child has this parent contract line
        }
    ))
);

export type TCEventTimeline = EventTimeline & { type: 'tc' };

export const isTCTimeline = (
  timeline: EventTimeline,
): timeline is TCEventTimeline => timeline.type === 'tc';

export type EventTimelineTimeDataOnly = Pick<
  EventTimeline,
  'type' | 'vessel'
> & {
  voyageGroups: {
    events: TimeData[];
  }[];
};

export type TimelineFocus = {
  timelineId: EventTimeline['id'];
  orderIds?: number[];
  shipmentIds?: Array<ClientId | null>;
  time?: {
    range: Range;
    startingWithinRangeOnly?: boolean; // Only include shipments that start within the timerange
  };
  navTarget?: ShipmentModalNavTarget; // used for default tab when opening modal
};

const getOrdersFromTimeDatas = (tds: TimeData[]): ScenarioOrder[] =>
  uniqBy(
    tds.filter(isCargoEventTimeData).map((td) => td.event.order),
    (o) => o.id,
  );

const mergeTimelinesTo = <T extends Events>(
  target: EventTimeline<T>,
  sources: EventTimeline<T>[],
): EventTimeline<T> => {
  target.voyageGroups.push(...sources.flatMap((s) => s.voyageGroups));
  return target;
};

export const generatePortCallsLegsAndWaits = (
  tds: TimeData[],
  locations: ScheduleDataContext['locations'],
): {
  portCalls: PortCallData[];
  legs: Array<Leg>;
  eventsAndLegs: Array<EventsAndLegs[number]>;
} => {
  /** About port calls: currently by the time this method is called, port call id's are already assigned by
   * the assignPortCallIdsToEvents method and the same operation here is unnecessary. This method only needs to generate
   * those PortCallData objects. However it seems better to ensure the id's are valid here anyway. Also this way any
   * events generated after id's were assigned onto persistable events get proper port call id's as well which may be useful.
   * */
  const ADD_IMPLICIT_WAIT_THRESHOLD = 10;

  let time = 0;
  const legs: Leg[] = [];
  const portCalls: PortCallData[] = [];
  let portCall: PortCallData | null = null;

  const eventsAndLegs: (Leg | TimeData)[] = [];

  // When events are separated from a single port call we need to make sure that both events can't still refer to the same port call
  const usedPortCallIds = new Set<ClientId>();
  const persistedPortCallIds = new Set<ClientId>();
  const l = tds.length;
  for (let i = 0; i < l; ++i) {
    const id = tds[i].event.portCallId;
    if (id) persistedPortCallIds.add(id);
  }

  for (let i = 0; i < l; ++i) {
    const td = tds[i];
    const prevTd = get(tds, i - 1) ?? null;
    const {
      start,
      end,
      event,
      index,
      distance,
      hoursAtSea,
      calculatedSeaDuration,
      laden,
      key,
    } = td;
    const locationId = getEventStartLocationId(event);
    const previousLocationId: number | null =
      prevTd && getEventEndLocationId(prevTd.event);

    if (portCall === null) {
      // Only the very first row ends up here
      portCall = {
        id: event.portCallId ?? null,
        locationId,
        tds: [td],
      };
      portCalls.push(portCall);
    }

    if (previousLocationId === null) {
      eventsAndLegs.push(td);
      time = end;
      continue;
    }
    if (previousLocationId !== locationId) {
      assertDefined(prevTd, 'Expected previous event for leg');
      const candidatePortCallId = event.portCallId ?? null;
      const alreadyUsed = candidatePortCallId
        ? usedPortCallIds.has(candidatePortCallId)
        : false;
      if (alreadyUsed) {
        console.error(
          `While generating legs encountered a new location with an already used port call. This hints at an invalid timeline (port call references not correctly fixed in timeline event entries in the scenario).`,
          { candidatePortCallId, locationId, time, index, event },
        );
      }
      const newPortCallId = alreadyUsed ? null : candidatePortCallId;
      portCall = { id: newPortCallId, locationId, tds: [] };
      if (!isOffHireEvent(event) && !isStaticEvent(event)) {
        event.portCallId = portCall.id;
      }
      portCalls.push(portCall);
      const from = getEventLocation(prevTd.event, locations, 'end');
      const to = getEventLocation(event, locations, 'start');
      const leg: Leg = {
        category: EventCategory.Leg,
        from: from,
        to,
        start: time,
        end: Math.min(start - 1, time + hToMs(hoursAtSea)),
        calculatedDuration: calculatedSeaDuration,
        index,
        distance,
        hoursAtSea,
        laden,
        seaDurationOverride: event.seaDurationOverride,
        channelDurationOverhead: isPortCallArrivalEventTimeData(td)
          ? td.channelDurationOverhead
          : null,
        avoidsChannels: 'legAvoidsChannels' in event && event.legAvoidsChannels,
        key: `leg-for-${key}`,
        event: {
          locationAId: from.id,
          locationBId: to.id,
          portCallId: portCall.id,
        },
        usedChannels: td.usedChannels,
      };
      eventsAndLegs.push(leg);
      legs.push(leg);
    } else {
      // Same location as previous event
      if (
        !isOffHireEvent(event) &&
        !isStaticEvent(event) &&
        portCall.id !== event.portCallId
      ) {
        // Use the port call from current event only if it's already persisted and the previous port call isn't
        if (
          event.portCallId !== null &&
          (portCall.id === null || !persistedPortCallIds.has(portCall.id))
        ) {
          portCall.id = event.portCallId;
          portCall.tds.forEach((td) => {
            if (!isOffHireEvent(td.event) && !isStaticEvent(td.event)) {
              td.event.portCallId = event.portCallId;
            }
          });
        } else {
          if (event.portCallId !== portCall.id) {
            console.warn(
              `Mismatching port call ids, timeline validation may have a bug`,
              { eventOriginalPortCall: event.portCallId, event, portCall },
            );
          }
          event.portCallId = portCall.id;
        }
      }
    }

    if (getEventEndLocationId(event) !== locationId) {
      // Clear port call on off-hire which changes location
      portCall = {
        id: event.portCallId ?? generateClientId(),
        locationId,
        tds: [],
      };
    }

    portCall.tds.push(td);
    time = Math.max(time, end + 1);
    eventsAndLegs.push(td);

    if (portCall.id !== null) {
      usedPortCallIds.add(portCall.id);
    }
  }

  const eventsAndLegsWithWaits: EventsAndLegs = [];

  time = 0;
  for (let i = 0; i < eventsAndLegs.length; ++i) {
    const e = eventsAndLegs[i];
    const { end } = e;
    if (i > 0) {
      const prevEndActivity = time;
      const { start, key, event } = e;
      if (prevEndActivity + ADD_IMPLICIT_WAIT_THRESHOLD < start) {
        const location =
          'from' in e ? e.from : getEventLocation(e.event, locations);
        const tmpWaitEvent: WaitEvent = {
          start: prevEndActivity + 1,
          end: start - 1,
          calculatedDuration: undefined,
          locationName: location.name ?? '',
          locationTimeZone: location.timezone ?? null,
          index: i,
          isGenerated: true,
          eventType: ImplicitEventType.IDLE,
          key: `wait-for-${key}`,
          category: EventCategory.Wait,
          event: {
            locationId: getEventLocationId(event),
            portCallId: event.portCallId,
          },
        };
        eventsAndLegsWithWaits.push(tmpWaitEvent);
      }
    }
    eventsAndLegsWithWaits.push(e);
    time = Math.max(time, end + 1);
  }

  return { legs, eventsAndLegs: eventsAndLegsWithWaits, portCalls };
};

export type TimelineVoyage<EventType extends Events = Events> = (
  | {
      shipment: ScenarioShipment;
      orderId: null;
    }
  | {
      shipment: null;
      orderId: number;
    }
) & {
  cargoEvents: TimeData<CargoEvent>[];
  implicitEvents: (Leg | WaitEvent)[];
  timeline: EventTimeline<EventType>;
};

export enum VoyageStatus {
  ONGOING = 'O',
  UPCOMING = 'U',
  COMPLETED = 'C',
}

export const getVoyages = (timeline: EventTimeline): TimelineVoyage[] => {
  const result: TimelineVoyage[] = [];

  for (const voyageGroup of timeline.voyageGroups) {
    const tds: TimeData[] = voyageGroup.events;
    const cargoEvents: TimeData<CargoEvent>[] =
      tds.filter(isCargoEventTimeData);

    const eventsByShipment = mapGroupBy(
      cargoEvents,
      (e) => e.event.order.shipmentId ?? null,
    );

    const noshipmentEventsByOrderId = mapGroupBy(
      eventsByShipment.get(null) ?? [],
      (e: TimeData<CargoEvent>) => e.event.order.id,
    );

    eventsByShipment.forEach((events, shipmentId) => {
      if (shipmentId === null) {
        // Orders without shipment handled separately
        return;
      }

      const implicitEvents = getOverlappingEvents(
        events,
        voyageGroup.eventsAndLegs.filter(isNotTimeData),
        true,
      );

      // Take shipment from the last event
      // They should be the same because events have the same shipmentId
      // events array always will be non-empty due to mapGroupBy implementation
      const shipment = events.slice().pop()?.event.order.shipment;
      assertDefined(shipment, 'shipment');

      result.push({
        shipment,
        orderId: null,
        cargoEvents: sortBy(events, (e) => e.start),
        implicitEvents,
        timeline,
      });
    });

    noshipmentEventsByOrderId.forEach((events, orderId) => {
      events.sort((a, b) => a.start - b.start);

      const implicitEvents = getOverlappingEvents(
        events,
        voyageGroup.eventsAndLegs.filter(isNotTimeData),
        true,
      );

      result.push({
        shipment: null,
        orderId,
        cargoEvents: sortBy(events, (e) => e.start),
        implicitEvents,
        timeline,
      });
    });
  }

  return result;
};

export const getVoyageStatus = (
  voyage: TimelineVoyage,
  portCallsById: Map<ClientId, ScenarioTimeline_PortCallFragment>,
  referenceTime: number = Date.now(),
): VoyageStatus => {
  const lastTd = voyage.cargoEvents[voyage.cargoEvents.length - 1];

  if (lastTd.event.portCallId !== null) {
    const lastPortCall = portCallsById.get(lastTd.event.portCallId);
    const sof = lastPortCall?.statementOfFactsByPortCallId.nodes[0];
    if (sof?.sailingTime || sof?.loadingCompleted) {
      return VoyageStatus.COMPLETED;
    }
  }

  return voyage.cargoEvents[0].start > referenceTime
    ? VoyageStatus.UPCOMING
    : VoyageStatus.ONGOING;
};

export const findVoyageGroupForEvent = (
  timeline: EventTimeline,
  event: Event,
): VoyageGroup | null => {
  const res = timeline.voyageGroups.find(
    (vg) => !!vg.events.find((td) => td.event === event),
  );
  if (!res) {
    console.error(
      `(findVoyageGroupForEvent) Attempted to find event`,
      event,
      `on the timeline ${timeline.id} but it was not found`,
    );
  }
  return res ?? null;
};

const collectDerivedTimelineEventsAndTotals = <
  T extends CoaTimeDataGroup | SpotTimeDataGroup,
  TK,
>(
  scheduleContext: ScheduleDataContext,
  groupedEvents: DefaultMap<TK, DefaultMap<number | null, T[]>>,
  topLevelId: TK,
  vesselId: number | null,
): VoyageGroup[] => {
  const result: VoyageGroup[] = [];

  groupedEvents
    .get(topLevelId)
    .get(vesselId)
    .forEach((tdGroup) => {
      const { timeData, totals, inputs } = tdGroup;
      const vessel = scheduleContext.vessels.get(vesselId ?? NaN) ?? null;

      const { portCalls, legs, eventsAndLegs } = generatePortCallsLegsAndWaits(
        timeData,
        scheduleContext.locations,
      );

      const vg: VoyageGroup = {
        events: timeData,
        eventsAndLegs,
        portCalls,
        legs,
        orders: getOrdersFromTimeDatas(timeData),
        totals,
        inputs,
        vessel,
      };

      result.push(vg);
    });

  return result;
};
export const getTimelines = (
  tcTimeDataByVessel: Map<number, TcTimeDataGroup>,
  coaTimeData: CoaTimeDataGroup[],
  spotTimeData: SpotTimeDataGroup[],
  coaContracts: readonly ScenarioTimeline_CoaFragment[],
  scheduleContext: ScheduleDataContext,
  currentUserOrgId: number | null | undefined,
  organizationsById: Map<number, ScenarioTimeline_OrganizationFragment>,
  collapsedTimelineIds: Set<EventTimeline['id']>,
  ignoreShipownerTimelines?: boolean, // Hotfix for hiding COA/Spot timelines from shipowner planning
  isShipOwner?: boolean,
): EventTimeline[] => {
  const { vessels } = scheduleContext;

  const timelines: EventTimeline[] = [];

  const showAllTimelines =
    typeof window !== 'undefined' && window.location.hash.includes('all');

  const currentUserIsBroker =
    currentUserOrgId && organizationsById.get(currentUserOrgId)?.broker;

  vessels.forEach((vessel) => {
    if (
      showAllTimelines ||
      vessel.organizationId === currentUserOrgId ||
      currentUserIsBroker
    ) {
      const { capacity, unit } = getPreferredCapacityQuantity(vessel);
      const timeDataGroup = tcTimeDataByVessel.get(vessel.id);

      const events = timeDataGroup?.timeData ?? [];
      const inputs = timeDataGroup?.inputs ?? null;
      const orders = getOrdersFromTimeDatas(events);

      const voyageGroup: VoyageGroup = {
        events: events,
        orders,
        ...generatePortCallsLegsAndWaits(events, scheduleContext.locations),
        totals: timeDataGroup?.totals ?? null,
        inputs,
        vessel,
      };

      const timeline: EventTimeline = {
        type: 'tc',
        vesselId: vessel.id,
        vessel,
        id: `vessel-${vessel.id}`,
        title: vessel.name ?? `Unnamed vessel ${vessel.id}`,
        subtitle: capacity && unit ? `${capacity} ${unit}` : 'Capacity N/A',
        voyageGroups: [voyageGroup],
        isPartial: false,
      };
      if (timeline.voyageGroups.length > 1) {
        throw new Error(`TC timelines can only have 1 voyage group.`);
      }
      timelines.push(timeline);
    }
  });

  const coaTimeDataByContractAndVessel = mapGroupBy2(
    coaTimeData,
    (tdGroup) => tdGroup.coaId,
    (tdGroup) => tdGroup.vesselId,
  );

  coaContracts.forEach((coa) => {
    if (
      isShipOwner &&
      ignoreShipownerTimelines &&
      coa.chartererOrgId !== currentUserOrgId
    ) {
      return;
    }

    const timelineId = `coa-${coa.id}`;
    const isCollapsed = collapsedTimelineIds.has(timelineId);

    const voyageGroups = collectDerivedTimelineEventsAndTotals(
      scheduleContext,
      coaTimeDataByContractAndVessel,
      coa.id,
      null,
    );

    const vesselTimelines: EventTimeline<CoaEvent>[] = [];

    const contractTimeline: EventTimeline<CoaEvent> = {
      type: 'coa',
      coaId: coa.id,
      coa,
      vessel: null,
      vesselId: null,
      ownerOrganizationId: coa.shipowner?.id ?? null,
      id: timelineId,
      title: `COA ${coa.shipowner?.name ?? ''}`,
      subtitle: coa.name ?? '',
      isPartial: false,
      canCollapse: true,
      isCollapsed: isCollapsed,
      voyageGroups,
      vesselTimelineIds: [],
    };

    timelines.push(contractTimeline);

    coa.vessels.nodes.filter(isDefined).forEach((coaVessel) => {
      const vessel = vessels.get(coaVessel.shipId);

      if (vessel) {
        const { capacity, unit } = getPreferredCapacityQuantity(vessel);

        const voyageGroups = collectDerivedTimelineEventsAndTotals(
          scheduleContext,
          coaTimeDataByContractAndVessel,
          coa.id,
          vessel.id,
        );

        const vesselTimeline: EventTimeline<CoaEvent> = {
          type: 'coa',
          coa,
          coaId: coa.id,
          vessel: vessel,
          vesselId: vessel.id,
          ownerOrganizationId: coa.shipowner?.id ?? null,
          id: `coa-${coa.id}-${vessel.id}`,
          title: vessel.name ?? 'Unnamed vessel',
          subtitle: capacity && unit ? `${capacity} ${unit}` : 'Capacity N/A',
          isPartial: false,
          canCollapse: false,
          isCollapsed: false,
          voyageGroups,
          contractTimelineId: contractTimeline.id,
        };

        vesselTimelines.push(vesselTimeline);
      }
    });

    if (isCollapsed) {
      mergeTimelinesTo(contractTimeline, vesselTimelines);
    } else {
      contractTimeline.vesselTimelineIds = vesselTimelines.map(
        (timeline) => timeline.id,
      );
      timelines.push(...vesselTimelines);
    }
  });

  const spotTimeDataByOrgAndVessel = mapGroupBy2(
    spotTimeData,
    (tdGroup) => tdGroup.shipownerOrgId,
    (tdGroup) => tdGroup.vesselId,
  );
  const emptyTimelines: EventTimeline<SpotEvent>[] = [];

  for (const [ownerOrgId, m] of spotTimeDataByOrgAndVessel.entries()) {
    if (
      isShipOwner &&
      ignoreShipownerTimelines &&
      ownerOrgId === currentUserOrgId
    ) {
      continue;
    }

    const timelineId = `spot-${ownerOrgId}`;
    const vesselTimelines: EventTimeline<SpotEvent>[] = [];

    const ownerOrgName =
      typeof ownerOrgId === 'number'
        ? organizationsById.get(ownerOrgId)?.name
        : undefined;

    const isCollapsed = collapsedTimelineIds.has(timelineId);

    const voyageGroups = collectDerivedTimelineEventsAndTotals(
      scheduleContext,
      spotTimeDataByOrgAndVessel,
      ownerOrgId,
      null,
    );

    const contractTimeline: EventTimeline<SpotEvent> = {
      type: 'spot',
      ownerOrganizationId: ownerOrgId,
      vessel: null,
      vesselId: null,
      id: timelineId,
      title: `SPOT ${ownerOrgName}`,
      subtitle: '',
      isPartial: false,
      canCollapse: true,
      isCollapsed,
      voyageGroups,
      vesselTimelineIds: [],
    };

    if (ownerOrgName) {
      timelines.push(contractTimeline);
    }

    for (const vesselId of m.keys()) {
      const vessel = vesselId
        ? (scheduleContext.vessels.get(vesselId) ?? null)
        : null;

      if (vessel) {
        const { capacity, unit } = getPreferredCapacityQuantity(vessel);

        const voyageGroups = collectDerivedTimelineEventsAndTotals(
          scheduleContext,
          spotTimeDataByOrgAndVessel,
          ownerOrgId,
          vesselId,
        );

        const vesselTimeline: EventTimeline<SpotEvent> = {
          type: 'spot',
          ownerOrganizationId: ownerOrgId,
          vessel,
          vesselId,
          id: `${timelineId}-${vesselId}`,
          title: vessel.name ?? 'Unnamed vessel',
          subtitle: capacity && unit ? `${capacity} ${unit}` : 'Capacity N/A',
          voyageGroups,
          isPartial: false,
          canCollapse: false,
          isCollapsed: false,
          contractTimelineId: contractTimeline.id,
        };

        vesselTimelines.push(vesselTimeline);
      } else if (vessel === null && ownerOrgId === null) {
        const emptyTimeline: EventTimeline<SpotEvent> = {
          type: 'spot',
          ownerOrganizationId: null,
          vessel: null,
          vesselId: null,
          id: 'spot-null-null',
          title: isShipOwner ? 'Relet TBN' : 'Undefined spot contract',
          subtitle: '',
          voyageGroups,
          isPartial: false,
          canCollapse: false,
          isCollapsed: false,
          contractTimelineId: '',
        };

        emptyTimelines.push(emptyTimeline);
      }
    }

    if (isCollapsed) {
      mergeTimelinesTo(contractTimeline, vesselTimelines);
    } else {
      contractTimeline.vesselTimelineIds = vesselTimelines.map(
        (timeline) => timeline.id,
      );
      timelines.push(...vesselTimelines);
    }
  }

  if (
    !timelines.find(
      (t) =>
        t.type == 'spot' &&
        t.ownerOrganizationId === null &&
        t.vesselId === null,
    )
  ) {
    if (
      !emptyTimelines.find(
        (t) =>
          t.type == 'spot' &&
          t.ownerOrganizationId === null &&
          t.vesselId === null,
      )
    ) {
      const emptyTimeline: EventTimeline<SpotEvent> = {
        type: 'spot',
        ownerOrganizationId: null,
        vessel: null,
        vesselId: null,
        id: 'spot-null-null',
        title: isShipOwner ? 'Relet TBN' : 'Undefined spot contract',
        subtitle: '',
        voyageGroups: [],
        isPartial: false,
        canCollapse: false,
        isCollapsed: false,
        contractTimelineId: '',
      };
      emptyTimelines.push(emptyTimeline);
    }
    timelines.push(...emptyTimelines);
  }

  const existingTimelineIds = new Set<string>();
  return timelines.filter((timeline) => {
    const alreadyExists = existingTimelineIds.has(timeline.id);
    if (alreadyExists) {
      console.error(
        `There is a duplicate group id on timelines: ${timeline.id} (vessel ${
          timeline.vessel?.name
        }, COA ${
          timeline.type === 'coa' && timeline.coa.name
        }). The latter group was filtered out but this is a sign of bad data. For example, a vessel has been added to a coa contract multiple times.`,
      );
    }
    existingTimelineIds.add(timeline.id);
    return !alreadyExists;
  });
};
